import { Icon } from '@design/components/icon/Icon';
import { removeMarkdown } from '@helpers';
import { formatDistanceStrict } from 'date-fns';
import { useMemo } from 'react';

import styles from './ChatCard.module.scss';

const MILLISECOND_A_DAY = 86400000;

export interface ChatCardProps {
  title: string;
  summary: string;
  updatedAt: string;
  searchString?: string;
}

export const ChatCard = ({
  title,
  summary,
  updatedAt,
  searchString,
}: ChatCardProps) => {
  const updatedAtDate = new Date(updatedAt);
  const shouldShowFullDate =
    new Date().valueOf() - updatedAtDate.valueOf() > MILLISECOND_A_DAY;

  const formattedDate = new Intl.DateTimeFormat(navigator.language).format(
    updatedAtDate
  );

  const trimmedSummary = useMemo(() => {
    return removeMarkdown(summary) ?? '';
  }, [summary]);

  const genHighlightedText = (text: string, searchString?: string) => {
    if (!searchString) {
      return text;
    }

    const regex = new RegExp(
      `(${searchString.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
      'gi'
    );
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (regex.test(part)) {
        return (
          <mark
            className={styles.mark}
            key={`${part}-${index}`}
            data-testid="highlighted"
          >
            {part}
          </mark>
        );
      } else {
        return <span key={`${part}-${index}`}>{part}</span>;
      }
    });
  };

  return (
    <div className={styles.chatCardWrapper}>
      <h5 className={styles.title}>{title}</h5>
      <div className={styles.summary}>
        {genHighlightedText(trimmedSummary, searchString)}
      </div>
      <div className={styles.timestampWrapper}>
        <Icon
          iconName="ClockFill"
          fillColor="var(--icon-soft)"
          width={12}
          height={12}
        />
        <span data-testid="updated-at">
          {shouldShowFullDate
            ? formattedDate
            : formatDistanceStrict(updatedAtDate, new Date(), {
                addSuffix: true,
              })}
        </span>
      </div>
    </div>
  );
};
